import { ref, reactive } from 'vue';

// 获取翻译函数
const getT = () => {
  // 这里我们需要从 Vue 应用中获取翻译函数
  // 由于 composable 不能直接访问 i18n，我们需要通过参数传入
  return window.__VUE_I18N__?.global?.t || ((key) => key);
};

// 状态变量
const projects = ref([]);
const selectedProject = ref('');
const loading = ref(false);

import { useCloudProvider } from './useCloudProvider.js';
// 云服务提供者相关
const { currentProvider, configLoaded } = useCloudProvider();


const resources = reactive({
  vms: [],
  disks: [],
  zones: []
});

const currentProjectId = ref('');

let makeRequest = fetch;

// 获取项目列表
const fetchProjects = async () => {
  // 等待云服务配置加载完成
  if (!configLoaded.value) {
    console.log('等待云服务配置加载...');
    return;
  }

  // AWS模式下不需要获取GCP项目
  if (currentProvider.value === 'aws') {
    console.log('AWS模式：跳过项目获取');
    return;
  }

  loading.value = true;
  try {
    // 首先尝试获取配置的项目列表
    const configuredResponse = await makeRequest('/api/projects/configured');
    if (configuredResponse.ok) {
      const configuredData = await configuredResponse.json();
      if (configuredData.projects && configuredData.projects.length > 0) {
        projects.value = configuredData.projects;
        return;
      }
    }

    // 如果没有配置的项目，尝试获取gcloud项目
    const gcloudResponse = await makeRequest('/api/gcloud/project');
    if (gcloudResponse.ok) {
      const gcloudData = await gcloudResponse.json();
      if (gcloudData.projectId) {
        projects.value = [gcloudData.projectId];
        return;
      }
    }

    // 最后尝试获取当前项目
    const currentResponse = await makeRequest('/api/projects');
    if (currentResponse.ok) {
      const currentData = await currentResponse.json();
      if (currentData.projectId) {
        projects.value = [currentData.projectId];
        return;
      }
    }

    // 如果都失败了，设置默认项目
    projects.value = ['your-project-id'];
  } catch (error) {
    console.error(getT()('composables.errors.loadProjectsFailed'), error);
    projects.value = ['your-project-id'];
  } finally {
    loading.value = false;
  }
};

// 选择项目
const selectProject = async (projectId) => {
  selectedProject.value = projectId;
  currentProjectId.value = projectId;
  await fetchResources(projectId);
};

// 获取资源
const fetchResources = async (projectId) => {
  if (!projectId) {return;}

  loading.value = true;
  try {
    await loadResources(projectId);
  } catch (error) {
    console.error(getT()('composables.errors.loadResourcesFailed'), error);
  } finally {
    loading.value = false;
  }
};

// 加载资源
const loadResources = async (projectId) => {
  currentProjectId.value = projectId;

  try {
    console.log('useResources: loading resources for project:', projectId);

    // 使用统一的请求函数
    const response = await makeRequest(`/api/resources/${projectId}`);

    const data = await response.json();

    console.log('useResources: API response:', data);

    if (response.ok) {
      resources.vms = data.vms || [];
      resources.disks = data.disks || [];
      resources.zones = data.zones || [];
      console.log('useResources: resources updated:', resources);
    } else {
      throw new Error(data.error || getT()('composables.errors.loadResourcesFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.errors.loadResourcesFailed'), error);
    throw error;
  }
};

// VM操作
const operateVM = async (action, instanceName, zone) => {
  if (!currentProjectId.value) {
    throw new Error(getT()('composables.errors.loadProjectFirst'));
  }

  const confirmMessage = {
    'start': getT()('composables.confirm.startVM'),
    'stop': getT()('composables.confirm.stopVM'),
    'delete': getT()('composables.confirm.deleteVM'),
    'resize': getT()('composables.confirm.resizeVM')
  };

  if (!confirm(confirmMessage[action])) {
    return;
  }

  try {
    // 根据云提供商选择不同的API端点
    const endpoint = currentProvider.value === 'aws' ? `/api/aws/vm/${action}` : `/api/vm/${action}`;

    const response = await makeRequest(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        projectId: currentProjectId.value,
        zone,
        instanceName
      })
    });

    if (response.ok) {
      // 2秒后刷新资源
      setTimeout(() => {
        loadResources(currentProjectId.value);
      }, 2000);
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || getT()('composables.errors.operationFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.logs.vmOperationFailed'), error);
    throw error;
  }
};

// 删除磁盘
const deleteDisk = async (diskName, zone) => {
  if (!currentProjectId.value) {
    throw new Error(getT()('composables.errors.loadProjectFirst'));
  }

  if (!confirm(getT()('composables.confirm.deleteDisk'))) {
    return;
  }

  try {
    // 根据云提供商选择不同的API端点
    const endpoint = currentProvider.value === 'aws' ? '/api/aws/volume/delete' : '/api/disk/delete';

    const response = await makeRequest(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        projectId: currentProjectId.value,
        zone,
        diskName,
        // AWS使用volumeId而不是diskName
        volumeId: currentProvider.value === 'aws' ? diskName : undefined
      })
    });

    if (response.ok) {
      setTimeout(() => loadResources(currentProjectId.value), 2000);
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || getT()('composables.errors.deleteFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.logs.deleteDiskFailed'), error);
    throw error;
  }
};

// 关联磁盘到VM
const attachDiskToVM = async (diskName, vmName, zone) => {
  if (!currentProjectId.value) {
    throw new Error(getT()('composables.errors.loadProjectFirst'));
  }

  try {
    // 根据云提供商选择不同的API端点
    const endpoint = currentProvider.value === 'aws' ? '/api/aws/volume/attach' : '/api/vm/attach-disk';

    const response = await makeRequest(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        projectId: currentProjectId.value,
        zone,
        instanceName: vmName,
        diskName,
        // AWS使用volumeId和instanceId
        volumeId: currentProvider.value === 'aws' ? diskName : undefined,
        instanceId: currentProvider.value === 'aws' ? vmName : undefined
      })
    });

    if (response.ok) {
      setTimeout(() => loadResources(currentProjectId.value), 2000);
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || getT()('composables.errors.attachFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.logs.attachDiskFailed'), error);
    throw error;
  }
};

// 分离磁盘从VM
const detachDiskFromVM = async (diskName, vmName, zone, device = null) => {
  if (!currentProjectId.value) {
    throw new Error(getT()('composables.errors.loadProjectFirst'));
  }

  if (!confirm(getT()('composables.confirm.detachDisk').replace('{vmName}', vmName).replace('{diskName}', diskName))) {
    return;
  }

  try {
    // 根据云提供商选择不同的API端点
    const endpoint = currentProvider.value === 'aws' ? '/api/aws/volume/detach' : '/api/vm/detach-disk';

    const requestBody = {
      projectId: currentProjectId.value,
      zone,
      instanceName: vmName,
      diskName
    };

    // AWS需要额外的参数
    if (currentProvider.value === 'aws') {
      requestBody.volumeId = diskName;
      requestBody.instanceId = vmName;
      if (device) {
        requestBody.device = device;
      }
    }

    const response = await makeRequest(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (response.ok) {
      setTimeout(() => loadResources(currentProjectId.value), 2000);
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || getT()('composables.errors.detachFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.logs.detachDiskFailed'), error);
    throw error;
  }
};

// 创建VM
const createVM = async (vmData) => {
  // 根据云提供商选择不同的API端点
  const endpoint = currentProvider.value === 'aws' ? '/api/aws/instances' : '/api/vm/create';

  // 为AWS映射字段名
  const requestData = { ...vmData };
  if (currentProvider.value === 'aws') {
    // 映射字段名以匹配后端期望
    requestData.instanceType = vmData.machineType;
    requestData.imageId = vmData.sourceImage;
    requestData.volumeSize = vmData.diskSizeGb;
    // 保留其他字段
    requestData.instanceName = vmData.instanceName;
    requestData.zone = vmData.zone;
    requestData.volumeType = vmData.volumeType;
  }

  const res = await makeRequest(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestData)
  });
  const data = await res.json();
  if (!res.ok) {
    throw data;
  }
  return data;
};

// 创建磁盘
const createDisk = async (diskData) => {
  if (!currentProjectId.value) {
    throw new Error(getT()('composables.errors.loadProjectFirst'));
  }

  try {
    // 根据云提供商选择不同的API端点
    const endpoint = currentProvider.value === 'aws' ? '/api/aws/volumes' : '/api/disk/create';

    const response = await makeRequest(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...diskData,
        projectId: currentProjectId.value
      })
    });

    if (response.ok) {
      return { success: true };
    } else {
      const error = await response.json();
      throw new Error(error.error || getT()('composables.errors.createFailed'));
    }
  } catch (error) {
    console.error(getT()('composables.logs.createDiskFailed'), error);
    throw error;
  }
};

// 获取VM状态
const getVMStatus = (vm) => {
  if (vm.status === 'RUNNING' || vm.status === 'running') {return getT()('composables.status.running');}
  if (vm.status === 'STOPPED' || vm.status === 'stopped') {return getT()('composables.status.stopped');}
  if (vm.status === 'TERMINATED' || vm.status === 'terminated') {return getT()('composables.status.terminated');}
  return vm.status || getT()('composables.status.unknown');
};

// 获取磁盘状态
const getDiskStatus = (disk) => {
  if (disk.status === 'READY') {return getT()('composables.status.ready');}
  if (disk.status === 'CREATING') {return getT()('composables.status.creating');}
  if (disk.status === 'RESTORING') {return getT()('composables.status.restoring');}
  return disk.status || getT()('composables.status.unknown');
};

// 批量获取VM使用情况
const getVMUsageBatch = async (vms) => {
  const runningVMs = vms.filter(vm => vm.status === 'RUNNING' || vm.status === 'running');

  console.log('useResources: 批量获取VM使用率，运行中VM数量:', runningVMs.length);

  if (runningVMs.length === 0) {
    return {};
  }

  try {
    const response = await makeRequest('/api/monitoring/vms/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        projectId: currentProjectId.value,
        vms: runningVMs,
        lookbackHours: 1
      })
    });

    if (response.ok) {
      const data = await response.json();
      console.log('useResources: 批量API响应:', data);

      const results = {};

      Object.keys(data).forEach(key => {
        const usageData = data[key];
        results[key] = {
          cpu: Math.round(usageData.current.cpu * 100),
          memory: Math.round(usageData.current.memory * 100),
          gpu: usageData.current.gpu !== null ? Math.round(usageData.current.gpu * 100) : null,
          hasData: usageData.hasData
        };
      });

      console.log('useResources: 处理后的结果:', results);
      return results;
    }
  } catch (error) {
    console.warn('批量获取VM监控数据失败:', error);
  }

  // 如果API调用失败，返回模拟数据
  console.log('useResources: 使用模拟数据');
  const results = {};
  runningVMs.forEach(vm => {
    const key = `${vm.name}-${vm.zone}`;
    results[key] = {
      cpu: Math.floor(Math.random() * 80) + 10,
      memory: Math.floor(Math.random() * 80) + 10,
      gpu: null,
      hasData: false
    };
  });

  return results;
};

// 获取单个VM使用情况（保持向后兼容）
const getVMUsage = async (vm) => {
  if (vm.status !== 'RUNNING' && vm.status !== 'running') {
    return {
      cpu: 0,
      memory: 0,
      gpu: null
    };
  }

  try {
    const response = await makeRequest(`/api/monitoring/vm/${vm.name}?projectId=${currentProjectId.value}&zone=${vm.zone}&lookbackHours=1`);
    if (response.ok) {
      const data = await response.json();
      return {
        cpu: Math.round(data.current.cpu * 100),
        memory: Math.round(data.current.memory * 100),
        gpu: data.current.gpu !== null ? Math.round(data.current.gpu * 100) : null,
        hasData: data.hasData
      };
    }
  } catch (error) {
    console.warn(getT()('composables.logs.cannotGetVMMonitoring').replace('{vmName}', vm.name), error);
  }

  return {
    cpu: Math.floor(Math.random() * 80) + 10,
    memory: Math.floor(Math.random() * 80) + 10,
    gpu: null,
    hasData: false
  };
};

// 获取磁盘使用情况
const getDiskUsage = async (disk) => {
  try {
    const response = await makeRequest(`/api/monitoring/disk/${disk.name}`);
    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.warn(getT()('composables.logs.cannotGetDiskMonitoring').replace('{diskName}', disk.name), error);
  }
  return {
    usage: Math.floor(Math.random() * 80) + 10
  };
};

// 清除资源
const clearResources = () => {
  resources.vms = [];
  resources.disks = [];
  resources.zones = [];
  currentProjectId.value = '';
  selectedProject.value = '';
};

// 获取访问令牌的函数
const getAccessToken = async () => {
  try {
    // 检查是否有 Cognito 用户
    if (window.userManager && window.userManager.getUser) {
      const cognitoUser = await window.userManager.getUser();
      if (cognitoUser && cognitoUser.access_token) {
        return cognitoUser.access_token;
      }
    }
    return null;
  } catch (error) {
    console.error('获取访问令牌失败:', error);
    return null;
  }
};

// 检查token是否即将过期（提前5分钟刷新）
const isTokenExpiringSoon = (token) => {
  try {
    const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;
    // 如果token在5分钟内过期，则认为是即将过期
    return expirationTime && (expirationTime - currentTime) < 300;
  } catch (error) {
    console.error('解析token失败:', error);
    return true; // 如果解析失败，认为需要刷新
  }
};

// 刷新token的函数
const refreshToken = async () => {
  try {
    if (window.userManager && window.userManager.getUser) {
      const cognitoUser = await window.userManager.getUser();
      if (cognitoUser) {
        // 使用oidc-client-ts的静默刷新功能
        const refreshedUser = await window.userManager.signinSilent();
        console.log('Token刷新成功');
        return refreshedUser.access_token;
      }
    }
    return null;
  } catch (error) {
    console.error('Token刷新失败:', error);
    // 如果静默刷新失败，可能需要重新登录
    throw error;
  }
};

// 通用的认证请求函数
const authenticatedFetch = async (url, options = {}) => {
  let token = await getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // 如果有访问令牌，添加到 Authorization 头
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // 发送请求
  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include' // 包含 cookie
  });

  // 如果收到401错误，尝试刷新token并重试
  if (response.status === 401 && token) {
    console.log('收到401错误，尝试刷新token...');
    try {
      const refreshedToken = await refreshToken();
      if (refreshedToken) {
        // 更新headers中的token
        headers['Authorization'] = `Bearer ${refreshedToken}`;
        
        // 重新发送请求
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include'
        });
        
        console.log('Token刷新后重试请求，状态码:', response.status);
      } else {
        console.error('Token刷新失败，无法获取新token');
        // 触发重新登录
        if (window.userManager) {
          console.log('触发重新登录...');
          await window.userManager.signinRedirect();
        }
      }
    } catch (refreshError) {
      console.error('Token刷新过程中出错:', refreshError);
      // 如果刷新失败，可能需要重新登录
      if (window.userManager) {
        console.log('Token刷新失败，触发重新登录...');
        await window.userManager.signinRedirect();
      }
    }
  }

  return response;
};

export function useResources() {
  // 辅助函数：根据云提供商选择使用认证请求还是普通请求
  makeRequest = async (url, options = {}) => {
    if (currentProvider.value === 'aws') {
      return await authenticatedFetch(url, options);
    } else {
      return await fetch(url, options);
    }
  };
  return {
    // 状态变量
    projects,
    selectedProject,
    loading,
    resources,
    currentProjectId,

    // 函数
    fetchProjects,
    fetchResources,
    selectProject,
    loadResources,
    operateVM,
    deleteDisk,
    attachDiskToVM,
    detachDiskFromVM,
    createVM,
    createDisk,
    getVMStatus,
    getDiskStatus,
    getVMUsage,
    getVMUsageBatch,
    getDiskUsage,
    clearResources,
    makeRequest
  };
}
